import React, { useState, useEffect, useCallback } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { Connection, LAMPORTS_PER_SOL, Keypair, ComputeBudgetProgram, PublicKey, Transaction } from '@solana/web3.js';
import { useAdmin } from '../contexts/AdminContext';
import { 
  buildCreateDesignInstruction,
  buildMintPopperNFTInstruction,
  buildSetProductImageInstruction,
  createTransaction, 
  WALLETS,
  getPopperNFTPDA,
  getVotingRoundPDA,
  getMetadataAccountPDA,
  getMasterEditionPDA,
  getAssociatedTokenAddress,
  getCollectionAccounts,
  PROGRAM_ID,
} from '../utils/popperverseProgram';
import './Create.css';

// Helper function to compress/resize image for upload
// Vercel has 4.5MB limit, so we target ~2MB max
const compressImage = async (dataUrl, maxWidth = 1024, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
      
      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to JPEG for better compression (smaller than PNG)
      const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
      
      console.log(`[Create] Image compressed: ${Math.round(dataUrl.length / 1024)}KB -> ${Math.round(compressedDataUrl.length / 1024)}KB`);
      resolve(compressedDataUrl);
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
};

// Wallet for creation fees (from shared utils)
const CREATE_WALLET = WALLETS.CREATE;

// Creation fee in SOL (for NFT minting after generation)
const CREATION_FEE_SOL = 0.5;

// Generation fee in USD (converted to SOL dynamically)
const GENERATION_FEE_USD = 5.00;

// Base prompt function that takes theme from user input
const getBasePrompt = (theme) => {
  return `Popperverse Popcat, chibi cat in a big themed cat-eared hood framing the face. clothing articles like hats, helmets, wigs, etc can be layered over it or attached to it (large round head, small oval body, beady black eyes, tiny nose); clean 3 px tapered line art, soft cel-shading, bright saturated colours, plush rounded shapes; ${theme} outfit integrated into the hood and clothing, holding a matching prop; bold graphic shapes, simple textures; background soft gradient with tiny dots OR clean white with soft ground shadow; vibrant, cute, expressive, big exaggerated popping mouth.`;
};

// Large pool of prompt suggestions
const ALL_SUGGESTIONS = [
  // Classic characters
  { emoji: '🥷', label: 'Ninja', prompt: 'A ninja with a tiny black mask and throwing stars, stealthy but adorable' },
  { emoji: '🧜', label: 'Mermaid', prompt: 'A mermaid with shimmering scales, a tiny seashell crown, and flowing underwater fur' },
  { emoji: '🧙', label: 'Wizard', prompt: 'A wizard with a sparkly purple robe, magic wand, and a tiny pointed hat with stars' },
  { emoji: '🏴‍☠️', label: 'Pirate', prompt: 'A swashbuckling pirate with an eyepatch, tiny cutlass, and a treasure map' },
  { emoji: '🦸', label: 'Superhero', prompt: 'A superhero with a flowing cape, mask, and lightning bolt emblem on chest' },
  { emoji: '🧛', label: 'Vampire', prompt: 'A cute vampire with tiny fangs, a cape with red lining, and bat wings' },
  { emoji: '🤠', label: 'Cowboy', prompt: 'A cowboy with a big hat, sheriff badge, boots with spurs, and a lasso' },
  { emoji: '🧚', label: 'Fairy', prompt: 'A magical fairy with sparkly wings, flower crown, and a tiny wand with stars' },
  { emoji: '🏰', label: 'Knight', prompt: 'A brave knight with shiny armor, a tiny sword, and a dragon plushie' },
  { emoji: '👸', label: 'Princess', prompt: 'A royal princess with a sparkling tiara, flowing gown, and magic scepter' },
  { emoji: '🧝', label: 'Elf', prompt: 'A woodland elf with pointed ears, green tunic, bow and arrow, and nature magic' },
  { emoji: '🧟', label: 'Zombie', prompt: 'A cute zombie with stitches, tattered clothes, and a brain-shaped lollipop' },
  { emoji: '👻', label: 'Ghost', prompt: 'A friendly ghost with a translucent sheet, floating accessories, and spooky sparkles' },
  { emoji: '🤖', label: 'Robot', prompt: 'A cute robot with antenna, LED eyes, chrome body, and tiny gears' },
  
  // Music & Entertainment
  { emoji: '🎧', label: 'DJ', prompt: 'A DJ with headphones, neon shades, and a tiny turntable, ready to drop beats' },
  { emoji: '🎸', label: 'Rockstar', prompt: 'A rockstar with an electric guitar, leather jacket, and cool shades' },
  { emoji: '🎺', label: 'Jazz', prompt: 'A smooth jazz musician with a saxophone, beret, and cool shades' },
  { emoji: '🎭', label: 'Jester', prompt: 'A playful jester with a colorful hat with bells, juggling balls' },
  { emoji: '🎪', label: 'Circus', prompt: 'A circus ringmaster with a top hat, red coat, and tiny whip' },
  { emoji: '🎬', label: 'Director', prompt: 'A movie director with a beret, megaphone, and clapperboard' },
  { emoji: '🎤', label: 'Pop Star', prompt: 'A pop star with a sparkly microphone, glitter outfit, and stage lights' },
  { emoji: '🩰', label: 'Ballerina', prompt: 'A graceful ballerina with a pink tutu, ballet slippers, and sparkle dust' },
  { emoji: '🎹', label: 'Pianist', prompt: 'A classical pianist with a tuxedo, tiny piano keys, and musical notes floating' },
  
  // Space & Sci-Fi
  { emoji: '🚀', label: 'Astronaut', prompt: 'An astronaut floating in space with a cute helmet, galaxy reflections, and tiny jetpack' },
  { emoji: '👽', label: 'Alien', prompt: 'A friendly alien with big eyes, antennae, a tiny UFO, and cosmic colors' },
  { emoji: '🛸', label: 'Space Pilot', prompt: 'A starship pilot with a flight suit, holographic visor, and laser blaster' },
  { emoji: '🌟', label: 'Star Guardian', prompt: 'A celestial guardian with star-shaped accessories, cosmic cape, and glowing aura' },
  
  // Professions
  { emoji: '👨‍🍳', label: 'Chef', prompt: 'A master chef with a tall white hat, tiny whisk, and adorable apron' },
  { emoji: '🔬', label: 'Scientist', prompt: 'A mad scientist with goggles, lab coat, and bubbling test tubes' },
  { emoji: '👨‍🚒', label: 'Firefighter', prompt: 'A brave firefighter with a red helmet, hose, dalmatian plushie, and fire truck' },
  { emoji: '👨‍⚕️', label: 'Doctor', prompt: 'A friendly doctor with a stethoscope, white coat, and tiny medical bag' },
  { emoji: '👮', label: 'Police', prompt: 'A friendly police officer with a badge, cap, and tiny handcuffs' },
  { emoji: '👨‍🌾', label: 'Farmer', prompt: 'A happy farmer with overalls, straw hat, pitchfork, and baby chick' },
  { emoji: '👨‍🎨', label: 'Artist', prompt: 'A creative artist with a beret, paint palette, brushes, and colorful splashes' },
  { emoji: '📚', label: 'Librarian', prompt: 'A wise librarian with round spectacles, stack of books, and cozy sweater' },
  
  // Sports & Adventure
  { emoji: '🏄', label: 'Surfer', prompt: 'A cool surfer with board shorts, surfboard, and awesome wave hair' },
  { emoji: '⚽', label: 'Soccer', prompt: 'A soccer star with jersey, cleats, soccer ball, and championship trophy' },
  { emoji: '🏀', label: 'Basketball', prompt: 'A basketball player with jersey, sneakers, basketball, and sweatband' },
  { emoji: '🎿', label: 'Skier', prompt: 'A winter skier with goggles, puffy jacket, skis, and snowy backdrop' },
  { emoji: '🧗', label: 'Climber', prompt: 'A rock climber with harness, chalk bag, helmet, and mountain gear' },
  { emoji: '🏊', label: 'Swimmer', prompt: 'An Olympic swimmer with goggles, swim cap, and gold medal' },
  { emoji: '🌊', label: 'Ocean', prompt: 'An ocean explorer with diving gear, bubbles, and friendly fish companions' },
  
  // Nature & Elements
  { emoji: '🌸', label: 'Sakura', prompt: 'A Japanese-inspired design with cherry blossoms, kimono, and paper umbrella' },
  { emoji: '🍄', label: 'Mushroom', prompt: 'A forest spirit with a mushroom cap hat, leaf cloak, and tiny acorns' },
  { emoji: '❄️', label: 'Ice', prompt: 'An ice queen/king with an icy crown, snowflake patterns, and icy blue fur' },
  { emoji: '🔥', label: 'Fire', prompt: 'A fire elemental with flame hair, ember accessories, and warm orange glow' },
  { emoji: '🌈', label: 'Rainbow', prompt: 'A rainbow unicorn hybrid with pastel colors, sparkly horn, and cloud accessories' },
  { emoji: '⚡', label: 'Electric', prompt: 'An electric-powered hero with lightning bolts, glowing eyes, and battery pack' },
  { emoji: '🌺', label: 'Tropical', prompt: 'A tropical island cat with hibiscus flower, lei, coconut drink, and palm leaves' },
  { emoji: '🍂', label: 'Autumn', prompt: 'An autumn spirit with falling leaves, cozy scarf, pumpkin spice, and warm colors' },
  
  // Food & Fun
  { emoji: '🍕', label: 'Pizza', prompt: 'A pizza delivery hero with a pizza box, scooter helmet, and cheese cape' },
  { emoji: '🍦', label: 'Ice Cream', prompt: 'An ice cream vendor with a cute cone hat, sprinkles, and waffle accessories' },
  { emoji: '🍩', label: 'Donut', prompt: 'A sweet donut character with frosting hat, sprinkle accessories, and sugar magic' },
  { emoji: '🧁', label: 'Cupcake', prompt: 'A cupcake baker with frosting swirl hat, cherry on top, and baking tools' },
  { emoji: '🍔', label: 'Burger', prompt: 'A fast food mascot with burger hat, fry accessories, and ketchup cape' },
  { emoji: '🍣', label: 'Sushi', prompt: 'A sushi chef with rice hat, chopsticks, wasabi, and cute fish friends' },
  { emoji: '☕', label: 'Coffee', prompt: 'A barista with coffee cup hat, latte art skills, and cozy cafe vibes' },
  
  // Gaming & Tech
  { emoji: '🎮', label: 'Gamer', prompt: 'A gamer with a headset, game controller, and pixel-art shirt' },
  { emoji: '💻', label: 'Hacker', prompt: 'A cool hacker with holographic screens, hoodie, and matrix-style code rain' },
  { emoji: '🕹️', label: 'Arcade', prompt: 'A retro arcade champion with 8-bit style, joystick, and high score crown' },
  { emoji: '📱', label: 'Influencer', prompt: 'A social media star with ring light, phone, and lots of heart emojis' },
  
  // Costumes & Creatures
  { emoji: '🦖', label: 'Dino', prompt: 'Wearing a cute dinosaur costume hoodie with spikes and tiny tail' },
  { emoji: '🎃', label: 'Pumpkin', prompt: 'A spooky but cute pumpkin costume with glowing eyes and candy bag' },
  { emoji: '🐉', label: 'Dragon', prompt: 'A baby dragon with tiny wings, fire breath, and treasure hoard' },
  { emoji: '🦄', label: 'Unicorn', prompt: 'A majestic unicorn with rainbow mane, sparkly horn, and magical aura' },
  { emoji: '🐺', label: 'Werewolf', prompt: 'A cute werewolf with fluffy fur, full moon backdrop, and howling pose' },
  { emoji: '🦊', label: 'Kitsune', prompt: 'A mystical kitsune with multiple tails, fox mask, and spirit flames' },
  { emoji: '🐼', label: 'Panda', prompt: 'A cozy panda with bamboo snack, sleepy eyes, and black and white markings' },
  { emoji: '🦁', label: 'Lion', prompt: 'A brave lion with majestic mane, crown, and royal cape' },
  
  // Cultural & Historical
  { emoji: '🏺', label: 'Greek', prompt: 'An ancient Greek hero with toga, laurel wreath, and golden shield' },
  { emoji: '⚔️', label: 'Viking', prompt: 'A fierce viking with horned helmet, battle axe, and viking ship' },
  { emoji: '🗡️', label: 'Samurai', prompt: 'An honorable samurai with katana, traditional armor, and cherry blossoms' },
  { emoji: '🏛️', label: 'Pharaoh', prompt: 'An Egyptian pharaoh with golden headdress, staff, and hieroglyphics' },
  { emoji: '🎎', label: 'Geisha', prompt: 'An elegant geisha with ornate kimono, fan, and traditional makeup' },
  
  // Seasonal & Holiday
  { emoji: '🎅', label: 'Santa', prompt: 'Santa Claus with red suit, white beard, gift bag, and reindeer friend' },
  { emoji: '🐰', label: 'Easter', prompt: 'An Easter bunny with basket of eggs, spring flowers, and pastel colors' },
  { emoji: '🎆', label: 'New Year', prompt: 'A New Year celebration with party hat, confetti, champagne, and fireworks' },
  { emoji: '💕', label: 'Valentine', prompt: 'A cupid with heart arrows, pink wings, and lots of love hearts' },
  { emoji: '🍀', label: 'Lucky', prompt: 'A lucky leprechaun with green hat, pot of gold, and four-leaf clover' },
];

const Create = () => {
  const { publicKey, sendTransaction, signTransaction, connected } = useWallet();
  const { connection } = useConnection();
  const { isAdmin } = useAdmin(); // Use global admin context
  const [prompt, setPrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isMinting, setIsMinting] = useState(false);
  const [error, setError] = useState(null);
  const [designName, setDesignName] = useState('');
  const [designDescription, setDesignDescription] = useState('');
  const [showMintForm, setShowMintForm] = useState(false);
  const [generationHistory, setGenerationHistory] = useState([]);
  
  // Admin manual upload state (local to Create page)
  const [showManualUpload, setShowManualUpload] = useState(false);
  const [manualImageUrl, setManualImageUrl] = useState('');
  const [manualImageName, setManualImageName] = useState('');
  const [manualImageFile, setManualImageFile] = useState(null);
  const [isAddingCandidate, setIsAddingCandidate] = useState(false);
  
  // Admin NFT minting state (for featured products)
  const [showAdminMint, setShowAdminMint] = useState(false);
  const [nftMintFile, setNftMintFile] = useState(null);
  const [nftMintUrl, setNftMintUrl] = useState('');
  const [nftName, setNftName] = useState('');
  const [nftNameInput, setNftNameInput] = useState(''); // User's raw input without suffix
  const [nftDescription, setNftDescription] = useState('');
  const [productImageUrl, setProductImageUrl] = useState('');
  const [creatorWalletAddress, setCreatorWalletAddress] = useState('');
  const [isMintingNFT, setIsMintingNFT] = useState(false);
  const [mintedNFTAddress, setMintedNFTAddress] = useState('');
  
  // Handle NFT name input with auto-append "Popper" suffix
  const handleNftNameChange = (e) => {
    let input = e.target.value;
    
    // Limit input to 24 characters (leaves 8 chars for " Popper")
    if (input.length > 24) {
      input = input.substring(0, 24);
    }
    
    setNftNameInput(input);
    
    // Auto-append " Popper" if user typed something
    if (input.trim()) {
      setNftName(input.trim() + ' Popper');
    } else {
      setNftName('');
    }
  };
  
  // Minting mode toggle - true = full NFT mint, false = legacy create_design
  // eslint-disable-next-line no-unused-vars
  const [useNFTMinting, setUseNFTMinting] = useState(true); // VotingRound initialized!
  
  // Current voting round (loaded from voting-round.json)
  const [currentVotingRound, setCurrentVotingRound] = useState(1);
  
  // SOL price for dynamic USD conversion
  const [solPrice, setSolPrice] = useState(null);
  const [isPaying, setIsPaying] = useState(false);

  // Random suggestions state
  const [currentSuggestions, setCurrentSuggestions] = useState([]);

  // Shuffle and pick random suggestions
  const refreshSuggestions = useCallback(() => {
    const shuffled = [...ALL_SUGGESTIONS].sort(() => Math.random() - 0.5);
    setCurrentSuggestions(shuffled.slice(0, 4));
  }, []);

  // Initialize suggestions on mount
  useEffect(() => {
    refreshSuggestions();
  }, [refreshSuggestions]);

  // Fetch current voting round from blockchain
  useEffect(() => {
    const loadActiveRoundFromChain = async () => {
      if (!connection) return;
      
      try {
        console.log('[Create] Fetching active voting round from chain...');
        
        // Derive all PDAs first (no RPC calls needed)
        const VOTING_ROUND_SEED = Buffer.from('voting_round');
        const pdas = [];
        for (let i = 1; i <= 20; i++) {
          const [pda] = PublicKey.findProgramAddressSync(
            [VOTING_ROUND_SEED, Buffer.from([i])],
            PROGRAM_ID
          );
          pdas.push(pda);
        }
        
        // Single batch RPC call instead of 20 sequential calls
        const accounts = await connection.getMultipleAccountsInfo(pdas);
        
        for (let i = 0; i < accounts.length; i++) {
          const info = accounts[i];
          if (!info) continue;
          
          const data = info.data;
          const roundNumber = data[8];
          const hasWinner = data[25] === 1;
          
          // Dynamic offset for is_active due to Borsh Option<Pubkey> compact serialization
          // None = 1 byte, Some = 1 + 32 bytes
          const postWinnerOffset = hasWinner ? 58 : 26;
          const isActiveOnChain = data[postWinnerOffset + 33] === 1;
          
          // If this round is active ON-CHAIN, use it
          if (isActiveOnChain) {
            console.log(`[Create] Found active round ${roundNumber} on chain`);
            setCurrentVotingRound(roundNumber);
            return;
          }
        }
        
        // If no active round found, default to 1
        console.log('[Create] No active voting round found on chain, defaulting to 1');
        setCurrentVotingRound(1);
        
      } catch (err) {
        console.error('[Create] Failed to fetch voting round from chain:', err);
        setCurrentVotingRound(1);
      }
    };
    loadActiveRoundFromChain();
  }, [connection]);

  // Fetch SOL price for USD conversion
  useEffect(() => {
    const fetchSolPrice = async () => {
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
        const data = await response.json();
        setSolPrice(data.solana.usd);
      } catch (err) {
        console.error('[Create] Failed to fetch SOL price:', err);
        setSolPrice(200); // Fallback price
      }
    };
    fetchSolPrice();
    // Refresh every 5 minutes
    const interval = setInterval(fetchSolPrice, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Calculate generation fee in SOL
  const getGenerationFeeSol = () => {
    if (!solPrice) return null;
    return (GENERATION_FEE_USD / solPrice).toFixed(4);
  };

  // Connection is provided by wallet adapter via useConnection()

  // Generate image using API (works on Vercel and locally)
  const generateImage = async () => {
    if (!prompt.trim()) {
      setError('Please enter a description for your Popper design');
      return;
    }

    // Check wallet connection (unless admin mode)
    if (!isAdmin) {
      if (!connected || !publicKey) {
        setError('Please connect your wallet to generate images');
        return;
      }
      
      const generationFeeSol = getGenerationFeeSol();
      if (!generationFeeSol) {
        setError('Unable to calculate fee - please try again');
        return;
      }
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Step 1: Process payment (unless admin mode)
      let paymentSignature = null;
      
      if (!isAdmin) {
        setIsPaying(true);
        const generationFeeSol = parseFloat(getGenerationFeeSol());
        const amountLamports = Math.floor(generationFeeSol * LAMPORTS_PER_SOL);
        
        console.log('[Create] Processing generation fee:', generationFeeSol, 'SOL (~$', GENERATION_FEE_USD, ')');
        
        // Build a simple SOL transfer to CREATE_WALLET
        const { SystemProgram, Transaction } = await import('@solana/web3.js');
        
        const transferIx = SystemProgram.transfer({
          fromPubkey: publicKey,
          toPubkey: CREATE_WALLET,
          lamports: amountLamports,
        });
        
        const transaction = new Transaction().add(transferIx);
        
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;
        transaction.feePayer = publicKey;
        
        // Send transaction and wait for confirmation
        paymentSignature = await sendTransaction(transaction, connection);
        
        await connection.confirmTransaction({
          signature: paymentSignature,
          blockhash,
          lastValidBlockHeight,
        });
        
        console.log('[Create] ✅ Payment confirmed:', paymentSignature);
        setIsPaying(false);
      }
      
      // Step 2: Generate the image
      // Send user's theme/prompt directly - API will construct full prompt with base template
      console.log('[Create] Sending request to /api/generate-image with theme:', prompt.substring(0, 50));
      
      // Use relative path - works on Vercel serverless and local dev
      const response = await fetch('/api/generate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt: prompt }),
      });

      console.log('[Create] Response status:', response.status);
      
      // Get response as text first to handle non-JSON responses
      const responseText = await response.text();
      console.log('[Create] Response text length:', responseText.length);
      console.log('[Create] Response preview:', responseText.substring(0, 200));
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        console.error('[Create] Failed to parse response as JSON:', parseErr.message);
        console.error('[Create] Raw response:', responseText);
        throw new Error(`Server returned invalid response: ${responseText.substring(0, 100)}`);
      }

      if (!response.ok) {
        console.error('[Create] API error response:', data);
        // Check if there's a suggestion (for IP/copyright issues)
        if (data.suggestion) {
          throw new Error(`${data.error}\n\n💡 Try instead: "${data.suggestion}"`);
        }
        throw new Error(data.error || data.details || 'Failed to generate image');
      }
      
      if (data.image_url) {
        console.log('[Create] ✅ Image generated successfully');
        setGeneratedImage(data.image_url);
        
        // Add to history (store full prompt from API response)
        setGenerationHistory(prev => [...prev, {
          image_url: data.image_url,
          prompt: data.prompt || prompt,
          theme: data.theme || prompt,
          timestamp: new Date().toISOString(),
        }]);
      } else {
        console.error('[Create] No image_url in response:', data);
        throw new Error('No image returned from API');
      }
    } catch (err) {
      console.error('[Create] Image generation error:', err);
      setError(err.message || 'Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
      setIsPaying(false);
    }
  };

  // Start fresh with a new design
  const startNewDesign = () => {
    setGeneratedImage(null);
    setPrompt('');
    setDesignName('');
    setDesignDescription('');
    setShowMintForm(false);
    setError(null);
  };

  // Handle manual image file selection (admin only)
  const handleManualImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setManualImageFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setManualImageUrl(previewUrl);
    }
  };

  // Add candidate manually (admin only)
  // NOTE: This adds to a local JSON structure. In production, you'll need to:
  // 1. Copy the image to public/Design/Candidates/
  // 2. Update public/candidates.json manually or via a server endpoint
  const addManualCandidate = async () => {
    if (!manualImageName.trim()) {
      alert('Please enter a name for the candidate');
      return;
    }
    if (!manualImageUrl && !manualImageFile) {
      alert('Please provide an image URL or upload a file');
      return;
    }

    setIsAddingCandidate(true);
    
    try {
      // Load current candidates
      const response = await fetch('/candidates.json');
      const data = response.ok ? await response.json() : { roundNumber: currentVotingRound, candidates: [] };
      
      // Generate new ID
      const maxId = data.candidates.reduce((max, c) => Math.max(max, c.id), 0);
      const newId = maxId + 1;
      
      // Determine frame color (alternating)
      const frame = newId % 2 === 1 ? 'orange' : 'blue';
      
      // Create new candidate entry
      const newCandidate = {
        id: newId,
        name: manualImageName.trim(),
        image: manualImageFile 
          ? `/Design/Candidates/${newId}.png`  // Expected path after manual copy
          : manualImageUrl,
        frame: frame,
        createdAt: new Date().toISOString(),
        addedBy: 'admin'
      };
      
      // Add to candidates array
      data.candidates.push(newCandidate);
      
      // Display instructions for manual steps
      console.log('=== MANUAL CANDIDATE ADDITION ===');
      console.log('New candidate data:', JSON.stringify(newCandidate, null, 2));
      console.log('\nTo complete the addition:');
      console.log(`1. Save the image as: public/Design/Candidates/${newId}.png`);
      console.log('2. Update public/candidates.json with:');
      console.log(JSON.stringify(data, null, 2));
      console.log('3. Commit and push to git');
      
      // Show success with instructions
      alert(
        `✅ Candidate "${manualImageName}" prepared!\n\n` +
        `Next steps:\n` +
        `1. Save image as: public/Design/Candidates/${newId}.png\n` +
        `2. Update public/candidates.json (see console for JSON)\n` +
        `3. Git commit and push\n\n` +
        `Check browser console for the full JSON to copy.`
      );
      
      // Reset form
      setManualImageName('');
      setManualImageUrl('');
      setManualImageFile(null);
      setShowManualUpload(false);
      
    } catch (err) {
      console.error('Error adding candidate:', err);
      alert('Failed to prepare candidate: ' + err.message);
    } finally {
      setIsAddingCandidate(false);
    }
  };

  // Handle admin NFT file upload
  const handleNFTFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setNftMintFile(file);
      const previewUrl = URL.createObjectURL(file);
      setNftMintUrl(previewUrl);
    }
  };

  // Mint featured product NFT directly (admin only)
  const mintAdminNFT = async () => {
    if (!nftName.trim()) {
      alert('Please enter an NFT name');
      return;
    }
    if (!nftMintFile && !nftMintUrl) {
      alert('Please provide an NFT image');
      return;
    }
    if (!creatorWalletAddress.trim()) {
      alert('Please enter a creator wallet address (royalty recipient)');
      return;
    }
    
    // Validate creator wallet
    let creatorPubkey;
    try {
      creatorPubkey = new PublicKey(creatorWalletAddress.trim());
    } catch (err) {
      alert('Invalid creator wallet address');
      return;
    }
    
    if (!connected || !publicKey) {
      alert('Please connect your admin wallet to mint NFTs');
      return;
    }

    setIsMintingNFT(true);
    setMintedNFTAddress('');
    
    try {
      // Convert image to base64
      let imageBase64;
      let imageMimeType;
      
      if (nftMintFile) {
        const reader = new FileReader();
        const fileData = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(nftMintFile);
        });
        imageBase64 = fileData.split(',')[1];
        imageMimeType = nftMintFile.type;
      } else {
        const response = await fetch(nftMintUrl);
        const blob = await response.blob();
        imageMimeType = blob.type;
        const reader = new FileReader();
        const fileData = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        imageBase64 = fileData.split(',')[1];
      }
      
      console.log('[Create] Uploading to Arweave...');
      
      // Upload to Arweave
      const arweaveResponse = await fetch('/api/upload-to-arweave', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageBase64,
          imageMimeType,
          name: nftName.trim(),
          description: nftDescription.trim() || `Popperverse Featured NFT: ${nftName}`,
          creatorWallet: creatorPubkey.toBase58(),
          prompt: '(Featured product - admin minted)',
          votingRound: 0,
        }),
      });
      
      if (!arweaveResponse.ok) {
        const errData = await arweaveResponse.json();
        throw new Error(errData.error || 'Arweave upload failed');
      }
      
      const { metadataUri } = await arweaveResponse.json();
      console.log('[Create] Metadata URI:', metadataUri);
      
      // Generate mint keypair
      const mintKeypair = Keypair.generate();
      console.log('[Create] Mint:', mintKeypair.publicKey.toBase58());
      
      // Derive required PDAs for admin mint
      // Token account is owned by creator_wallet, payer (admin) pays for everything
      const [tokenAccount, metadataAccount, masterEdition, popperNft] = await Promise.all([
        getAssociatedTokenAddress(creatorPubkey, mintKeypair.publicKey),
        getMetadataAccountPDA(mintKeypair.publicKey),
        getMasterEditionPDA(mintKeypair.publicKey),
        getPopperNFTPDA(mintKeypair.publicKey),
      ]);
      
      // Get collection accounts for verification (required)
      const { collectionMint, collectionMetadata, collectionMasterEdition } = getCollectionAccounts();
      
      // Debug log all parameters
      console.log('[Create] Building mint instruction with params:', {
        payer: publicKey.toBase58(),
        creatorWallet: creatorPubkey.toBase58(),
        nftMint: mintKeypair.publicKey.toBase58(),
        name: nftName.trim().substring(0, 32),
        symbol: 'POPPER',
        uri: metadataUri,
        collectionAuthority: publicKey.toBase58(),
      });
      
      // Build mint instruction with collection verification
      // Note: Admin mints use round 0 (perpetual admin round)
      const votingRoundAccount = await getVotingRoundPDA(0);
      
      const mintIx = buildMintPopperNFTInstruction({
        payer: publicKey,
        creatorWallet: creatorPubkey,
        nftMint: mintKeypair.publicKey,
        tokenAccount,
        metadataAccount,
        masterEdition,
        popperNft,
        votingRoundAccount, // Use current active round
        createWallet: CREATE_WALLET,
        collectionMint,
        collectionMetadata,
        collectionMasterEdition,
        collectionAuthority: publicKey, // Admin wallet is collection authority
        name: nftName.trim().substring(0, 32),
        symbol: 'POPPER',
        uri: metadataUri,
        creationFeeLamports: 0, // Admin mints are free
      });
      
      const computeBudgetIx = ComputeBudgetProgram.setComputeUnitLimit({ units: 400_000 });
      const transaction = new Transaction();
      transaction.add(computeBudgetIx);
      transaction.add(mintIx);
      
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;
      
      console.log('[Create] Signing transaction with wallet...');
      
      try {
        // Sign with wallet first
        const signedTx = await signTransaction(transaction);
        
        // Then partially sign with mint keypair
        signedTx.partialSign(mintKeypair);
        
        console.log('[Create] Sending transaction...');
        
        // Serialize and send
        const rawTransaction = signedTx.serialize();
        const signature = await connection.sendRawTransaction(rawTransaction, {
          skipPreflight: false,
          preflightCommitment: 'confirmed',
        });
        console.log('[Create] Transaction sent:', signature);
        
        await connection.confirmTransaction(signature, 'confirmed');
        console.log('[Create] Transaction confirmed');
        
        console.log('[Create] NFT minted:', mintKeypair.publicKey.toBase58());
        setMintedNFTAddress(mintKeypair.publicKey.toBase58());
      } catch (txError) {
        console.error('[Create] Transaction error:', txError);
        console.error('[Create] Error message:', txError.message);
        console.error('[Create] Error stack:', txError.stack);
        
        // Try to get simulation logs
        if (txError.logs) {
          console.error('[Create] Transaction logs:', txError.logs);
        }
        
        // Check for more error details
        if (txError.transactionLogs) {
          console.error('[Create] Transaction logs:', txError.transactionLogs);
        }
        
        if (txError.simulationError) {
          console.error('[Create] Simulation error:', txError.simulationError);
        }
        
        // Print full error object for debugging
        console.error('[Create] Full error object:', JSON.stringify(txError, Object.getOwnPropertyNames(txError)));
        
        throw txError;
      }
      
      // Set product image if provided
      if (productImageUrl.trim()) {
        try {
          console.log('[Create] Setting product image...');
          const setImageIx = buildSetProductImageInstruction({
            authority: publicKey,
            nftMint: mintKeypair.publicKey,
            productImageUri: productImageUrl.trim(),
          });
          
          const setImageTx = createTransaction({ instructions: [setImageIx] });
          const { blockhash: blockhash2 } = await connection.getLatestBlockhash();
          setImageTx.recentBlockhash = blockhash2;
          setImageTx.feePayer = publicKey;
          
          const setImageSig = await sendTransaction(setImageTx, connection);
          await connection.confirmTransaction(setImageSig, 'confirmed');
          console.log('[Create] Product image set');
        } catch (imgErr) {
          console.error('[Create] Product image error:', imgErr);
          alert('NFT minted but failed to set product image. You can set it in the Shop page.');
        }
      }
      
      alert(
        `✅ NFT Minted Successfully!\n\n` +
        `Mint: ${mintKeypair.publicKey.toBase58()}\n` +
        `Creator: ${creatorWalletAddress}\n` +
        `${productImageUrl ? 'Product image set ✓' : 'Set product image in Shop'}\n\n` +
        `Use this mint address in the Shop page to link it to a featured product.`
      );
      
      // Reset form but keep address visible
      setNftName('');
      setNftNameInput('');
      setNftDescription('');
      setProductImageUrl('');
      setCreatorWalletAddress('');
      setNftMintFile(null);
      setNftMintUrl('');
      
    } catch (error) {
      console.error('[Create] Mint error:', error);
      alert('Failed to mint NFT: ' + error.message);
    } finally {
      setIsMintingNFT(false);
    }
  };

  // Pay creation fee and submit design
  const submitDesign = async () => {
    if (!connected || !publicKey) {
      setError('Please connect your wallet first');
      return;
    }

    if (!designName.trim()) {
      setError('Please enter a name for your design');
      return;
    }

    if (!generatedImage) {
      setError('Please generate an image first');
      return;
    }

    setIsMinting(true);
    setError(null);

    try {
      const amountLamports = Math.floor(CREATION_FEE_SOL * LAMPORTS_PER_SOL);
      
      if (useNFTMinting) {
        // ============================================
        // NEW FLOW: Full NFT Minting with Arweave
        // ============================================
        
        // Step 1: Compress and upload to Arweave
        console.log('[Create] Compressing image for upload...');
        
        // Compress the image to fit within Vercel's 4.5MB limit
        const compressedImage = await compressImage(generatedImage, 1024, 0.85);
        
        // Parse the data URL: "data:image/jpeg;base64,/9j/4A..."
        const dataUrlMatch = compressedImage.match(/^data:(.+);base64,(.+)$/);
        if (!dataUrlMatch) {
          throw new Error('Invalid image format - expected data URL');
        }
        const imageMimeType = dataUrlMatch[1];  // e.g., "image/jpeg"
        const imageBase64 = dataUrlMatch[2];     // The actual base64 data
        
        console.log(`[Create] Uploading to Arweave (${Math.round(imageBase64.length / 1024)}KB)...`);
        
        const arweaveResponse = await fetch('/api/upload-to-arweave', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageBase64: imageBase64,
            imageMimeType: imageMimeType,
            name: `${designName} Popper`.substring(0, 32),
            description: designDescription || `A unique Popperverse NFT: ${designName} Popper`,
            creatorWallet: publicKey.toBase58(),
            prompt: prompt,
            votingRound: currentVotingRound,
          }),
        });
        
        if (!arweaveResponse.ok) {
          const errData = await arweaveResponse.json();
          console.error('[Create] Arweave error:', errData);
          throw new Error(errData.error + (errData.debug ? ` (debug: ${JSON.stringify(errData.debug)})` : ''));
        }
        
        const { metadataUri } = await arweaveResponse.json();
        console.log('[Create] Arweave metadata URI:', metadataUri);
        
        // Step 2: Generate a new mint keypair
        const mintKeypair = Keypair.generate();
        console.log('[Create] New mint address:', mintKeypair.publicKey.toBase58());
        
        // Step 3: Derive all required PDAs
        const [
          tokenAccount,
          metadataAccount,
          masterEdition,
          popperNft,
          votingRoundAccount
        ] = await Promise.all([
          getAssociatedTokenAddress(publicKey, mintKeypair.publicKey),
          getMetadataAccountPDA(mintKeypair.publicKey),
          getMasterEditionPDA(mintKeypair.publicKey),
          getPopperNFTPDA(mintKeypair.publicKey),
          getVotingRoundPDA(currentVotingRound),
        ]);
        
        // Check if voting round exists before attempting to mint
        const votingRoundInfo = await connection.getAccountInfo(votingRoundAccount);
        if (!votingRoundInfo) {
          throw new Error(`There isn't an active voting round to submit a Popper to. Please wait for the next voting round to begin.`);
        }
        
        // Get collection accounts for verification (required)
        const { collectionMint, collectionMetadata, collectionMasterEdition } = getCollectionAccounts();
        
        // Step 4: Build mint instruction
        // For community mints: user is both payer and creator (they mint to themselves)
        const mintIx = buildMintPopperNFTInstruction({
          payer: publicKey,
          creatorWallet: publicKey, // Community creators mint to themselves
          nftMint: mintKeypair.publicKey,
          tokenAccount,
          metadataAccount,
          masterEdition,
          popperNft,
          votingRoundAccount,
          createWallet: CREATE_WALLET,
          collectionMint,
          collectionMetadata,
          collectionMasterEdition,
          collectionAuthority: publicKey, // Creator is also collection authority
          name: `${designName} Popper`.substring(0, 32),
          symbol: 'POPPER',
          uri: metadataUri,
          creationFeeLamports: amountLamports,
        });
        
        // Step 5: Create and send transaction with compute budget
        const transaction = createTransaction(mintIx);
        
        // Request additional compute units (400K should be plenty for NFT minting with metadata)
        const computeBudgetIx = ComputeBudgetProgram.setComputeUnitLimit({
          units: 400_000,
        });
        // Prepend compute budget instruction
        transaction.instructions.unshift(computeBudgetIx);
        
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;
        transaction.feePayer = publicKey;
        
        // Add mint keypair as signer
        transaction.partialSign(mintKeypair);
        
        // Log transaction details for debugging
        console.log('[Create] Transaction details:', {
          votingRound: currentVotingRound,
          votingRoundAccount: votingRoundAccount.toBase58(),
          popperNft: popperNft.toBase58(),
          mintKeypair: mintKeypair.publicKey.toBase58(),
          feePayer: publicKey.toBase58(),
        });
        
        // Simulate transaction first to get detailed error
        try {
          const simulation = await connection.simulateTransaction(transaction);
          console.log('[Create] Simulation result:', simulation);
          if (simulation.value.err) {
            console.error('[Create] Simulation error:', simulation.value.err);
            console.error('[Create] Simulation logs:', simulation.value.logs);
            throw new Error(`Transaction simulation failed: ${JSON.stringify(simulation.value.err)}\nLogs: ${simulation.value.logs?.join('\n')}`);
          }
        } catch (simErr) {
          console.error('[Create] Simulation failed:', simErr);
          throw simErr;
        }
        
        const signature = await sendTransaction(transaction, connection);
        
        await connection.confirmTransaction({
          signature,
          blockhash,
          lastValidBlockHeight,
        });
        
        console.log('[Create] NFT minted successfully:', signature);
        
        alert(`🎉 NFT Minted! Your Popper "${designName}" is now on-chain!\n\nMint: ${mintKeypair.publicKey.toBase58().substring(0, 20)}...\n\nView on Solana Explorer`);
        startNewDesign();
        setShowMintForm(false);
        
      } else {
        // ============================================
        // LEGACY FLOW: create_design instruction
        // ============================================
        
        const productNumber = Date.now() % 1000000;

        const createIx = buildCreateDesignInstruction({
          creator: publicKey,
          createWallet: CREATE_WALLET,
          designName: designName.substring(0, 50),
          productNumber,
          amountLamports,
        });

        const transaction = createTransaction(createIx);

        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;
        transaction.feePayer = publicKey;

        const signature = await sendTransaction(transaction, connection);
        
        await connection.confirmTransaction({
          signature,
          blockhash,
          lastValidBlockHeight,
        });

        // Design is now stored on-chain as an NFT - no need for separate database submission
        console.log(`Design minted on-chain with signature: ${signature}`);
        
        alert(`🎉 Success! Your Popper design "${designName}" has been minted as an NFT and submitted for community voting!\n\nTransaction: ${signature}`);
        startNewDesign();
        setShowMintForm(false);
      }

    } catch (err) {
      console.error('Submission error:', err);
      
      // Check for specific error codes
      let errorMessage = err.message || 'Failed to submit design. Please try again.';
      
      // Check if it's a voting round not initialized error (error code 3012)
      if (err.message && (err.message.includes('3012') || err.message.includes('AccountNotInitialized'))) {
        errorMessage = "There isn't an active voting round to submit a Popper to. Please wait for the next voting round to begin.";
      }
      // Check if logs indicate voting round issue
      else if (err.logs && err.logs.some(log => log.includes('voting_round_account') && log.includes('AccountNotInitialized'))) {
        errorMessage = "There isn't an active voting round to submit a Popper to. Please wait for the next voting round to begin.";
      }
      // For other transaction failures, show user-friendly message but log details
      else if (err.logs) {
        console.error('Transaction logs:', err.logs);
        errorMessage = 'Transaction failed. Please check the console for details.';
      }
      
      setError(errorMessage);
    } finally {
      setIsMinting(false);
    }
  };

  return (
    <div className="create-page">
      {/* Admin Mode Banner */}
      {isAdmin && (
        <div className="admin-banner">
          <div className="admin-banner-content">
            <span>🛠️ ADMIN MODE - Free image generation enabled</span>
            <div className="admin-action-buttons">
              <button 
                className="admin-action-btn"
                onClick={() => {
                  setShowManualUpload(!showManualUpload);
                  if (!showManualUpload) setShowAdminMint(false);
                }}
              >
                {showManualUpload ? '✕ Close' : '📤 Add Candidate'}
              </button>
              <button 
                className="admin-action-btn primary"
                onClick={() => {
                  setShowAdminMint(!showAdminMint);
                  if (!showAdminMint) setShowManualUpload(false);
                }}
              >
                {showAdminMint ? '✕ Close' : '🎨 Mint Featured Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Manual Upload Panel */}
      {isAdmin && showManualUpload && (
        <div className="admin-upload-panel">
          <h3>📤 Add Candidate Manually</h3>
          <p>Add an image directly to the voting candidates without NFT minting.</p>
          
          <div className="upload-form">
            <div className="form-group">
              <label>Candidate Name:</label>
              <input
                type="text"
                value={manualImageName}
                onChange={(e) => setManualImageName(e.target.value)}
                placeholder="e.g., Pirate Popper"
              />
            </div>
            
            <div className="form-group">
              <label>Image Source:</label>
              <div className="image-source-options">
                <div className="option">
                  <label>Upload File:</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleManualImageSelect}
                  />
                </div>
                <div className="option-divider">OR</div>
                <div className="option">
                  <label>Image URL:</label>
                  <input
                    type="text"
                    value={manualImageFile ? '' : manualImageUrl}
                    onChange={(e) => {
                      setManualImageUrl(e.target.value);
                      setManualImageFile(null);
                    }}
                    placeholder="https://..."
                    disabled={!!manualImageFile}
                  />
                </div>
              </div>
            </div>
            
            {(manualImageUrl || manualImageFile) && (
              <div className="preview-container">
                <label>Preview:</label>
                <img 
                  src={manualImageUrl} 
                  alt="Preview" 
                  className="manual-preview"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            
            <button
              className="submit-manual-btn"
              onClick={addManualCandidate}
              disabled={isAddingCandidate || !manualImageName.trim()}
            >
              {isAddingCandidate ? 'Processing...' : '➕ Add to Candidates'}
            </button>
            
            <p className="upload-note">
              ⚠️ Note: After adding, you'll need to manually save the image file 
              to <code>public/Design/Candidates/</code> and update <code>candidates.json</code>.
              Check the browser console for the JSON to copy.
            </p>
          </div>
        </div>
      )}

      {/* Admin NFT Minting Panel */}
      {isAdmin && showAdminMint && (
        <div className="admin-upload-panel nft-mint-panel">
          <h3>🎨 Mint Featured Product NFT</h3>
          <p>
            Mint a complete on-chain Popper NFT (bypasses voting). The NFT will be sent to the creator wallet 
            and can be immediately listed as a featured product in the Shop.
          </p>
          
          <div className="upload-form">
            <div className="form-group">
              <label>NFT Name: *</label>
              <input
                type="text"
                value={nftNameInput}
                onChange={handleNftNameChange}
                placeholder="e.g., SOL Dev"
                maxLength={24}
              />
              <div className="name-preview">
                <span className="preview-label">Full Name:</span>
                <span className="preview-text">{nftName || '(type to preview)'}</span>
              </div>
              <span className="char-count">{nftNameInput.length}/24 (+ " Popper")</span>
            </div>
            
            <div className="form-group">
              <label>Description:</label>
              <textarea
                value={nftDescription}
                onChange={(e) => setNftDescription(e.target.value)}
                placeholder="A unique Popperverse plushie..."
                rows={3}
              />
            </div>
            
            <div className="form-group">
              <label>Creator Wallet Address: *</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <input
                  type="text"
                  value={creatorWalletAddress}
                  onChange={(e) => setCreatorWalletAddress(e.target.value)}
                  placeholder="Wallet address to receive the NFT and royalties"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => setCreatorWalletAddress(WALLETS.DEFAULT_CREATOR.toBase58())}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Use Default
                </button>
              </div>
              <p className="help-text">
                This wallet will own the NFT and receive 100% of royalties initially. 
                You (admin) will pay for minting. On transfer, royalties split 25% creator / 75% new holder.
              </p>
            </div>
            
            <div className="form-group">
              <label>NFT Artwork: *</label>
              <div className="image-source-options">
                <div className="option">
                  <label>Upload File:</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleNFTFileSelect}
                  />
                </div>
                <div className="option-divider">OR</div>
                <div className="option">
                  <label>Image URL:</label>
                  <input
                    type="text"
                    value={nftMintFile ? '' : nftMintUrl}
                    onChange={(e) => {
                      setNftMintUrl(e.target.value);
                      setNftMintFile(null);
                    }}
                    placeholder="https://..."
                    disabled={!!nftMintFile}
                  />
                </div>
              </div>
            </div>
            
            <div className="form-group">
              <label>Product Photo URL: (optional)</label>
              <input
                type="text"
                value={productImageUrl}
                onChange={(e) => setProductImageUrl(e.target.value)}
                placeholder="https://... (photo of physical plushie)"
              />
              <p className="help-text">
                Optional: Photo of the actual physical product. Shows by default, NFT art shows on hover.
                Can also be set later in the Shop page.
              </p>
            </div>
            
            {(nftMintUrl || nftMintFile) && (
              <div className="preview-container">
                <label>NFT Artwork Preview:</label>
                <img 
                  src={nftMintUrl} 
                  alt="NFT Preview" 
                  className="manual-preview"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            
            <button
              className="submit-manual-btn primary"
              onClick={mintAdminNFT}
              disabled={isMintingNFT || !nftName.trim() || (!nftMintFile && !nftMintUrl) || !creatorWalletAddress.trim() || !connected}
            >
              {isMintingNFT ? '⏳ Minting NFT...' : '✨ Mint Featured Product NFT'}
            </button>
            
            {mintedNFTAddress && (
              <div className="minted-address">
                <label>✅ NFT Minted Successfully!</label>
                <div className="address-display">
                  <input 
                    type="text" 
                    value={mintedNFTAddress} 
                    readOnly 
                    onClick={(e) => e.target.select()}
                  />
                  <button 
                    className="copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(mintedNFTAddress);
                      alert('Mint address copied to clipboard!');
                    }}
                  >
                    📋 Copy
                  </button>
                </div>
                <p className="help-text">
                  <strong>Next steps:</strong><br />
                  1. Copy the mint address above<br />
                  2. Go to the Shop page<br />
                  3. Click a featured product card (admin mode)<br />
                  4. Paste the mint address to link this NFT
                </p>
              </div>
            )}
            
            <p className="upload-note">
              💡 <strong>What this does:</strong> Creates a full on-chain PopperNFT with all the same structure as voting winners, 
              but bypasses the candidate/voting system. Perfect for featured products that you want to list immediately.
            </p>
          </div>
        </div>
      )}

      <div className="splash-container">
        <div className="hero-content">
          <h1>Create Your Popper Design</h1>
          <p className="subtitle">Use AI to design your unique Popperverse plushie and earn royalties!</p>
        </div>
        
        <div className="cat-playground">
          <img src="/Design/silhouette.png" alt="Cat Silhouette" className="cat-silhouette" />
        </div>

        <div className="create-content">
          {/* Design Generator Section */}
          <div className="generator-section">
            <div className="generator-container">
              {/* Left: Input & Controls */}
              <div className="generator-controls">
                <div className="info-section">
                  <h2>🎨 AI Design Generator</h2>
                  <p>Describe your dream Popper plushie and our AI will bring it to life!</p>
                </div>

                <div className="prompt-input-container">
                  <label htmlFor="prompt">Describe Your Popper:</label>
                  <textarea
                    id="prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="e.g., A cosmic space cat with galaxy patterns, wearing a tiny astronaut helmet, with sparkly purple fur and star-shaped eyes..."
                    rows={4}
                    disabled={isGenerating}
                  />
                </div>

                <div className="generator-buttons">
                  {/* Generation Fee Info */}
                  {!isAdmin && (
                    <div className="generation-fee-info">
                      <span className="fee-label">Generation Fee:</span>
                      <span className="fee-amount">
                        {solPrice ? (
                          <>${GENERATION_FEE_USD.toFixed(2)} ({getGenerationFeeSol()} SOL)</>
                        ) : (
                          <>Loading price...</>
                        )}
                      </span>
                    </div>
                  )}
                  {isAdmin && (
                    <div className="admin-badge">🔓 Admin Mode - Free Generation</div>
                  )}
                  
                  <button 
                    className="generate-btn primary"
                    onClick={generateImage}
                    disabled={isGenerating || !prompt.trim() || (!isAdmin && !connected)}
                  >
                    {isPaying ? (
                      <>
                        <span className="spinner"></span>
                        Processing Payment...
                      </>
                    ) : isGenerating ? (
                      <>
                        <span className="spinner"></span>
                        Generating...
                      </>
                    ) : !connected && !isAdmin ? (
                      <>🔗 Connect Wallet to Generate</>
                    ) : isAdmin ? (
                      <>✨ Generate Design (Free)</>
                    ) : (
                      <>✨ Pay & Generate Design</>
                    )}
                  </button>
                  {generatedImage && (
                    <button 
                      className="generate-btn outline"
                      onClick={startNewDesign}
                      disabled={isGenerating}
                    >
                      🆕 Start Over
                    </button>
                  )}
                </div>

                {error && (
                  <div className="error-message">
                    <span>⚠️</span> {error}
                  </div>
                )}

                {/* Example Prompts */}
                <div className="example-prompts">
                  <div className="inspiration-header">
                    <h4>💡 Need inspiration? Try these:</h4>
                    <button 
                      className="refresh-suggestions-btn"
                      onClick={refreshSuggestions}
                      title="Get new suggestions"
                    >
                      🔄
                    </button>
                  </div>
                  <div className="prompt-chips">
                    {currentSuggestions.map((suggestion, index) => (
                      <button 
                        key={index}
                        onClick={() => setPrompt(suggestion.prompt)}
                      >
                        {suggestion.emoji} {suggestion.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Image Preview */}
              <div className="generator-preview">
                {generatedImage ? (
                  <div className="image-container">
                    <img 
                      src={generatedImage} 
                      alt="Generated Popper Design" 
                      className="generated-image"
                    />
                    <div className="image-actions">
                      <a 
                        href={generatedImage}
                        download="my-popper-design.png"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="action-btn download"
                      >
                        📥 Download
                      </a>
                      <button 
                        className="action-btn submit"
                        onClick={() => setShowMintForm(true)}
                        disabled={!connected}
                      >
                        🚀 Submit for Voting
                      </button>
                    </div>
                    {!connected && (
                      <p className="connect-hint">Connect your wallet to submit your design</p>
                    )}
                  </div>
                ) : (
                  <div className="preview-placeholder">
                    <div className="placeholder-icon">🎨</div>
                    <p>Your design will appear here</p>
                    <span className="placeholder-hint">Enter a description and click "Generate Design"</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit Form Modal */}
          {showMintForm && (
            <div className="mint-modal-overlay" onClick={() => !isMinting && setShowMintForm(false)}>
              <div className="mint-modal" onClick={e => e.stopPropagation()}>
                <button className="close-btn" onClick={() => !isMinting && setShowMintForm(false)}>×</button>
                
                <h2>🚀 Submit Your Design</h2>
                <p>Your design will be entered into community voting. If it wins, you'll earn royalties on every sale!</p>

                <div className="mint-preview">
                  <img 
                    src={generatedImage} 
                    alt="Your Design" 
                  />
                </div>

                <div className="mint-form">
                  <div className="form-group">
                    <label htmlFor="designName">Design Name *</label>
                    <input
                      id="designName"
                      type="text"
                      value={designName}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Remove existing " Popper" suffix if present to avoid duplication
                        if (value.endsWith(' Popper')) {
                          value = value.slice(0, -7);
                        }
                        setDesignName(value);
                      }}
                      placeholder="e.g., Cosmic Space Cat"
                      maxLength={25}
                      disabled={isMinting}
                    />
                    <div className="name-preview">
                      <span className="preview-label">Will appear as:</span>
                      <span className="preview-text">{designName || 'Your Name'} Popper</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="designDescription">Description (optional)</label>
                    <textarea
                      id="designDescription"
                      value={designDescription}
                      onChange={(e) => setDesignDescription(e.target.value)}
                      placeholder="Tell the story of your Popper..."
                      rows={3}
                      maxLength={200}
                      disabled={isMinting}
                    />
                  </div>

                  <div className="fee-info">
                    <div className="fee-row">
                      <span>Creation Fee:</span>
                      <span className="fee-amount">{CREATION_FEE_SOL} SOL</span>
                    </div>
                    <p className="fee-note">This fee covers storage and enters you into the voting pool</p>
                  </div>

                  {error && (
                    <div className="error-message">
                      <span>⚠️</span> {error}
                    </div>
                  )}

                  <button 
                    className="mint-btn"
                    onClick={submitDesign}
                    disabled={isMinting || !designName.trim()}
                  >
                    {isMinting ? (
                      <>
                        <span className="spinner"></span>
                        Submitting...
                      </>
                    ) : (
                      <>💎 Pay {CREATION_FEE_SOL} SOL & Submit</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Generation History */}
          {generationHistory.length > 1 && (
            <div className="history-section">
              <h3>📜 Your Designs</h3>
              <div className="history-grid">
                {generationHistory.map((item, index) => (
                  <div 
                    key={index} 
                    className={`history-item ${item.image_url === generatedImage ? 'active' : ''}`}
                    onClick={() => {
                      setGeneratedImage(item.image_url);
                      setPrompt(item.prompt);
                    }}
                  >
                    <img 
                      src={item.image_url} 
                      alt={`Design ${index + 1}`}
                    />
                    <span>#{index + 1}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info Sections */}
          <div className="info-grid">
            <div className="info-section">
              <h2>🗳️ Community Voting</h2>
              <p>
                Once submitted, your design enters the community voting system. Holders vote with SOL, 
                and the most popular designs get manufactured as official Popperverse plushies.
              </p>
            </div>

            <div className="info-section highlight">
              <h2>💰 Earn Royalties</h2>
              <p>
                If your design wins and becomes a real plushie, you'll receive royalties on every sale!
              </p>
              <div className="royalty-details">
                <div className="royalty-item">
                  <span className="royalty-icon">✨</span>
                  <span>5% on every plushie sold</span>
                </div>
                <div className="royalty-item">
                  <span className="royalty-icon">🏆</span>
                  <span>Credit as official designer</span>
                </div>
                <div className="royalty-item">
                  <span className="royalty-icon">🎁</span>
                  <span>Free sample of your design</span>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h2>📋 Design Guidelines</h2>
              <ul className="guidelines-list">
                <li>Must be a cat-themed Popper character</li>
                <li>Family-friendly and appropriate for all ages</li>
                <li>Original designs only - no copyrighted characters</li>
                <li>One submission per voting period</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Create;
